import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Link from "../assets/icons/link.svg";
import Github from "../assets/icons/github.svg";
import Box from "../assets/icons/box.svg";

const StyledCard = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  height: 100%;
  text-align: initial;
  background: var(--black-regular);
  padding: var(--mb-2);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.11), 0 2px 2px rgba(0, 0, 0, 0.11),
    0 4px 4px rgba(0, 0, 0, 0.11), 0 6px 8px rgba(0, 0, 0, 0.11),
    0 8px 16px rgba(0, 0, 0, 0.11);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      transform: translateY(-7px);
      box-shadow: 0 2px 1px rgba(0, 0, 0, 0.09), 0 4px 2px rgba(0, 0, 0, 0.09),
        0 8px 4px rgba(0, 0, 0, 0.09), 0 16px 8px rgba(0, 0, 0, 0.09),
        0 32px 16px rgba(0, 0, 0, 0.09);
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .card-content {
    display: flex;
    flex-direction: column;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--mb-2);

    .card-icon {
      fill: var(--primary);
      width: var(--icon-size);
      height: var(--icon-size);
    }

    .card-links {
      a {
        .link-icon,
        .github-icon {
          fill: var(--gray-dark);
        }

        &:hover {
          .link-icon,
          .github-icon {
            fill: var(--primary);
          }
        }
      }
    }
  }

  .card-title {
    color: var(--white);
    font-size: var(--normal-font-size);
    font-family: var(--body-font);
    font-weight: var(--font-semi);
    margin-bottom: var(--mb-1);

    a {
      position: static;
      color: var(--white);

      &:hover,
      &:focus {
        color: var(--primary);
      }

      &:before {
        content: "";
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  .card-description {
    font-size: var(--smaller-font-size);
    font-family: var(--body-font);
    line-height: 24px;
    color: var(--gray-dark);
    margin-bottom: 0;
  }

  .card-additonal {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;

    li {
      font-size: var(--small-font-size);
      font-family: var(--body-font-2);
      color: var(--gray-regular);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: var(--mb-2);
      }
    }
  }
`;

const Card = ({ title, description, languages, external, github }) => {
  return (
    <StyledCard>
      <header>
        <div className="card-header">
          <Box class="card-icon" />
          <div className="card-links">
            {external ? (
              <a href={external} target="_blank" rel="noopener noreferrer">
                <Link className="link-icon" />
              </a>
            ) : null}
            {github ? (
              <a href={github} target="_blank" rel="noopener noreferrer">
                <Github className="github-icon" />
              </a>
            ) : null}
          </div>
        </div>
        <div className="card-content">
          <h4 className="card-title">
            <a href={external}>{title}</a>
          </h4>
          <p className="card-description">{description}</p>
        </div>
      </header>
      <footer>
        {Array.isArray(languages) && languages.length > 0 ? (
          <ul className="card-additonal">
            {languages.map((code) => {
              return <li key={`${title}-${code}`}>{code}</li>;
            })}
          </ul>
        ) : null}
      </footer>
    </StyledCard>
  );
};

Card.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  languages: PropTypes.array,
  external: PropTypes.string,
};

export default Card;
